import { ChangeDetectionStrategy, Component, DestroyRef, QueryList, TemplateRef, computed, contentChildren, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe, TitleCasePipe, UpperCasePipe, LowerCasePipe, CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { TableStorageService } from '@app/shared/services';
import { BooleanFormatOptions, ChipFormatOptions, ColumnDef, CustomFormatFunction, CustomFormatOptions, IconFormatOptions, PageChangeEvent, RowActionEvent, RowClickEvent } from '@app/shared/types/ui';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { DataTableColumnTemplateDirective } from '@app/core/ui/directives';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonModule
  ],
  providers: [CurrencyPipe, DatePipe, DecimalPipe, TitleCasePipe, UpperCasePipe, LowerCasePipe],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent<T> {
  private readonly tableStorage = inject(TableStorageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly paginator = viewChild(MatPaginator);
  private readonly sort = viewChild(MatSort);
  private readonly templateDirectives = contentChildren(DataTableColumnTemplateDirective, { descendants: true });
  readonly data = input<T[]>([]);
  readonly columns = input<ColumnDef[]>([]);
  readonly selectedRow = input<T | null>(null);
  readonly storageKey = input<string | undefined>(undefined);
  readonly pageSizeOptions = input<number[]>([5, 10, 25, 50, 100]);
  readonly initialPageSize = input<number>(10);
  readonly totalsSummary = input<Record<string, unknown>>({});
  readonly showTotalsRow = input<boolean>(true);
  readonly rowClass = input<(row: T) => Record<string, boolean>>(() => ({}));

  readonly rowSelectionChange = output<T[]>();
  readonly rowAction = output<RowActionEvent<T>>();
  readonly rowClick = output<RowClickEvent<T>>();
  readonly pageChange = output<PageChangeEvent>();
  readonly columnSettingsChange = output<ColumnDef[]>();

  readonly dataSource = signal(new MatTableDataSource<T>([]));
  readonly selection = signal(new SelectionModel<T>(true, []));
  readonly allColumns = signal<ColumnDef[]>([]);

  readonly displayedColumns = computed(() => this.allColumns().filter(c => c.visible).map(c => c.key));
  readonly visibleColumns = computed(() => this.allColumns().filter(c => c.visible));
  readonly length = computed(() => this.dataSource().data.length);

  private readonly columnTemplates = new Map<string, TemplateRef<unknown>>();
  private readonly footerTemplates = new Map<string, TemplateRef<unknown>>();
  private readonly customFormatters = new Map<string, CustomFormatFunction<T>>();

  constructor() {
    this.setupEffects();
  }


  private setupEffects(): void {
    effect(() => {
      this.columnTemplates.clear();
      this.footerTemplates.clear();
      this.templateDirectives().forEach(dir => {
        const key = dir.key().toLowerCase();
        if (dir.type() === 'footer') {
          this.footerTemplates.set(key, dir.templateRef);
        } else {
          this.columnTemplates.set(key, dir.templateRef);
        }
      });
    });

    effect(() => {
      const paginator = this.paginator();
      const sort = this.sort();
      const ds = this.dataSource();
      if (paginator) ds.paginator = paginator;
      if (sort) ds.sort = sort;
    });

    effect(() => {
      const newColumns = this.columns();
      if (newColumns.length > 0) this.initializeColumns(newColumns);
    });

    effect(() => {
      this.dataSource().data = this.data() ?? [];
    });
  }

  private initializeColumns(newColumns: ColumnDef[]): void {
    const key = this.storageKey();
    const finalColumns = key ? this.tableStorage.loadColumnSettings(key, newColumns) : [...newColumns];
    this.allColumns.set(finalColumns);
  }

  private processTemplates(directives: QueryList<DataTableColumnTemplateDirective>): void {
    this.columnTemplates.clear();
    this.footerTemplates.clear();
    directives.forEach(dir => {
      const key = dir.key().toLowerCase();
      if (dir.type() === 'footer') {
        this.footerTemplates.set(key, dir.templateRef);
      } else {
        this.columnTemplates.set(key, dir.templateRef);
      }
    });
  }

  registerFormatter(name: string, formatter: CustomFormatFunction<T>): void {
    this.customFormatters.set(name, formatter);
  }

  getColumnTemplate(key: string): TemplateRef<unknown> | undefined {
    return this.columnTemplates.get(key.toLowerCase());
  }

  getFooterTemplate(key: string): TemplateRef<unknown> | undefined {
    return this.footerTemplates.get(key.toLowerCase());
  }

  onRowClick(event: MouseEvent, row: T): void {
    this.rowClick.emit({ row, event });
  }

  getCellValue(row: T, key: string): unknown {
    if (!row) return '';
    const column = this.allColumns().find(c => c.key === key);
    const path = column?.propertyPath ?? key.split('.');
    return this.getNestedPropertyValue(row, path);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getNestedPropertyValue(obj: any, path: string[]): unknown {
    return path.reduce((acc, part) => acc?.[part], obj);
  }

  getTotalValue(key: string): unknown {
    return this.totalsSummary()[key] ?? '';
  }

  getColumnClass(column: ColumnDef): string {
    const classes: string[] = [];
    if (column.align) classes.push(`text-${column.align}`);
    if (['number', 'currency', 'percent'].includes(column.format as string)) {
      classes.push('is-numeric');
    }
    if (column.cssClass) classes.push(column.cssClass);
    return classes.join(' ');
  }

  getRowClasses(row: T): Record<string, boolean> {
    return this.rowClass()(row);
  }

  formatCustomValue(value: unknown, row: T, column: ColumnDef): string {
    const formatterName = (column.formatOptions as CustomFormatOptions)?.formatterFn;
    if (formatterName && this.customFormatters.has(formatterName)) {
      try {
        return String(this.customFormatters.get(formatterName)!(value, row, column) ?? '');
      } catch (e) {
        console.error(`Error in custom formatter '${formatterName}'`, e);
        return String(value ?? '');
      }
    }
    return String(value ?? '');
  }

  getChipClass(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as ChipFormatOptions;
    return options?.colorMap?.[String(value ?? '')] ?? options?.defaultColor ?? '';
  }

  getChipDisplayValue(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as ChipFormatOptions;
    if (options?.displayMap) {
      return options.displayMap[String(value ?? '')] ?? String(value ?? '');
    }
    return String(value ?? '');
  }

  getIconName(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as IconFormatOptions;
    return options?.iconMap?.[String(value ?? '')] ?? options?.defaultIcon ?? '';
  }

  getIconClass(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as IconFormatOptions;
    return options?.colorMap?.[String(value ?? '')] ?? options?.defaultColor ?? '';
  }

  getIconTooltip(row: T, column: ColumnDef): string {
    const options = column.formatOptions as IconFormatOptions;
    if (!options?.tooltipField) return '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return String(this.getNestedPropertyValue(row as any, options.tooltipField.split('.')) ?? '');
  }

  getBooleanDisplayType(column: ColumnDef): 'icon' | 'text' | 'chip' {
    return (column.formatOptions as BooleanFormatOptions)?.type ?? 'icon';
  }

  getBooleanText(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as BooleanFormatOptions;
    return value ? (options?.trueText ?? 'Sí') : (options?.falseText ?? 'No');
  }

  getBooleanIcon(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as BooleanFormatOptions;
    return value ? (options?.trueIcon ?? 'check_circle') : (options?.falseIcon ?? 'cancel');
  }

  getBooleanClass(value: unknown, column: ColumnDef): string {
    const options = column.formatOptions as BooleanFormatOptions;
    return value ? (options?.trueColor ?? 'primary') : (options?.falseColor ?? 'warn');
  }

  getCellTooltip(row: T, column: ColumnDef): string {
    if (column.format === 'icon') {
      return this.getIconTooltip(row, column);
    }
    return column.tooltip ?? '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected generateLink(template: string, row: Record<string, any>): string {
    const regex = /\{\{(\w+)\}\}/g;

    return template.replace(regex, (match, key) => {
      return row[key] ?? '';
    });
  }
}
